import React, { useState, useEffect, useRef } from 'react'
import uuid from 'uuid'
import {
  Affix,
  Dropdown,
  Menu,
  Empty,
  Radio,
  message,
  Space,
  Checkbox,
} from 'antd'
import { RichTextEditor } from '../../../components/RichTextEditor'
import {
  Button,
  FileUpload,
  Icon,
  Input,
  Textarea,
  useTraversal,
} from '@guillotinaweb/react-gmi'
import {
  SyncOutlined,
  CheckCircleTwoTone,
  SaveOutlined,
} from '@ant-design/icons'

import { AccordionCard } from '../../../components/accordion-card'
import * as dayjs from 'dayjs'
import { DnD } from '../../../common/dnd'
import { Field, BaseField } from '../../../common/field'
import { I18NField } from '../../../components/i18nfield'
import { ProductSelector } from '../../../components/product-selector'
import {
  allowToMoveOnlyInAccordionHeader,
  encodeAbsoluteLink,
  useClipboard,
} from '../../../utils'
import { getCdnUrl, isProd, setLangs, SHOPS } from '../../../consts'
import { ColorPicker } from '../../../components/color_picker'
import NLPreview from './Preview'
import { GenerateProductImageWithTriangle } from '../../../components/generate-product-image-with-triangle'
import { newsletterTranslator } from '../../../components/deeplTranslatorComponent'

const prefix = isProd() ? 'www' : 'stage'

const PRODUCT_ROW_NUM = 2

const btnTypesArray = [
  'btn',
  'btn_arrow',
  'btn_primary',
  'btn_primary_arrow',
  'tri_up',
  'tri_down',
]
const buttonTypes = new Set(btnTypesArray)
const disabledStatuses = new Set(['scheduled', 'verified'])

const contentNames = {
  b_multi_img: 'Banner',
  bd: 'Product banner',
  f: `Row of ${PRODUCT_ROW_NUM} products`,
  h1: 'Title',
  h2: 'Subtitle',
  p: 'Text block',
  w: 'Conditions',
  btn: 'Button',
  html: 'Html',
}

function getContentBrief(content, { lang, limit = 80 } = {}) {
  let contentBrief =
    content.btn_text?.[lang] ||
    content[lang] ||
    content.content?.join(' - ') ||
    ''

  if (contentBrief.length > limit) {
    contentBrief = contentBrief.slice(0, limit - 3) + '...'
  }

  return contentBrief
}

function NLProducts({
  sectionKey,
  content,
  limit,
  id,
  update,
  colors = [['Background color', 'bg_color']],
}) {
  const bdValue = content.referencia ? [content.referencia] : []
  const value =
    id === 'bd' ? bdValue : content.content.filter((c) => c && c !== '0')

  return (
    <>
      <ProductSelector
        id={sectionKey}
        type="refs"
        state={['selling']}
        products={value}
        onChange={(c) => {
          if (c.length > limit) {
            message.warning(
              `This section is limited to ${limit} products. If you want to add another one, you must first remove one.`
            )
            return
          }
          if (id === 'bd') return update({ ...content, referencia: c[0] || '' })
          update({ ...content, content: c })
        }}
      />
      {content.content?.length < limit && content.content.length !== 0 && (
        <div className="error-message">
          You must add another product, this section is for {limit} products
        </div>
      )}
      {colors.map(([label, field], index) => (
        <ColorPicker
          label={label}
          key={field}
          suggested={index === colors.length - 1}
          onChange={(e) => update({ ...content, [field]: e.target.value })}
          value={content[field] || '#c9d1ce'}
        />
      ))}
    </>
  )
}

function NewsletterSection({ id, sectionKey, content, lang, update }) {
  const [uploading, setUploading] = useState(false)
  const Ctx = useTraversal()
  const updateLang = updateI18nField()
  const updateTemplate = updateI18nField('template')
  const updateAlt = updateI18nField('alt')
  const updateLink = updateI18nField('link')
  const updatePath = updateI18nField('path')
  const updateImg = updateI18nField('image_src', true)

  function updateI18nField(field, initFull = false) {
    return (e) => {
      const v = e?.target?.value !== undefined ? e.target.value : e
      const fieldContent = content[field] || {}
      const c = field ? fieldContent : content
      const initialize = field && !Object.values(fieldContent).join('')
      const initial = initFull ? setLangs(v) : { ...setLangs(''), [lang]: v }

      const newContent = initialize ? { ...c, ...initial } : { ...c, [lang]: v }

      update(field ? { ...content, [field]: newContent } : newContent)
    }
  }

  async function onUploadBanner(file, callback = () => {}) {
    setUploading(true)
    const filename = `newsletters/${Ctx.state.context.id}`
    const res = await Ctx.client.upload(Ctx.path, file, filename)

    if (res.status >= 300) {
      message.error('Error uploading the image')
      setUploading(false)
      callback(false)
      return
    }

    const { path } = await res.json()
    const src = `/${path}`

    updateImg(src)
    setUploading(false)
    callback(true)
  }

  switch (id) {
    case 'h1':
    case 'tpp':
      return <Input onChange={updateLang} value={content[lang]} />
    case 'p':
    case 'p2':
      return (
        <RichTextEditor
          key={lang}
          id={sectionKey}
          value={content[lang] || ''}
          onChange={updateLang}
          height="200px"
          config={{
            buttons: 'bold,italic,underline,|,ul,ol,|,eraser',
          }}
        />
      )
    case 'h2':
    case 'w':
      return <Textarea onChange={updateLang} value={content[lang]} />
    case 'html':
      return (
        <Textarea onChange={updateTemplate} value={content.template?.[lang]} />
      )
    case 'f':
      return (
        <NLProducts
          content={content}
          id={id}
          limit={PRODUCT_ROW_NUM}
          sectionKey={sectionKey}
          update={update}
        />
      )
    case 'bd':
    case 'b_multi_img': {
      const link = `https://${prefix}.vinissimus.com/${content.link?.[lang]}`
      const img = content.image_src?.[lang] && (
        <img
          src={`${getCdnUrl()}${content.image_src?.[lang]}`}
          alt="Banner preview"
        />
      )

      return (
        <div className="banner-edit">
          {id === 'bd' && (
            <BaseField label="Product">
              <NLProducts
                content={content}
                id={id}
                limit={1}
                colors={[
                  ['Image background color', 'bg_color_img'],
                  ['Product background color', 'bg_color_product'],
                ]}
                sectionKey={sectionKey}
                update={update}
              />
            </BaseField>
          )}
          <div className="upload-or-genetare-btns">
            <FileUpload
              // Unmount after change lang to always fire onChange although you
              // upload the same image.
              key={lang}
              accept="image/*"
              onChange={onUploadBanner}
              label={uploading ? 'Uploading...' : 'Upload an image'}
            />
            <GenerateProductImageWithTriangle
              key={content.bg_color_img + content.referencia}
              defaultBgColor={content.bg_color_img}
              productRef={content.referencia}
              onChangeBgColor={(color) =>
                update({ ...content, bg_color_img: color })
              }
              onUpload={onUploadBanner}
            />
          </div>
          <Field label="Alt" value={content.alt?.[lang]} onChange={updateAlt} />
          {id !== 'bd' && (
            <Field
              label="Link"
              value={content.link?.[lang]}
              onChange={(link) => updateLink(encodeAbsoluteLink(link))}
            />
          )}
          <a
            className={id === 'bd' ? 'bd-preview' : ''}
            href={link}
            target="_blank"
            rel="noopener noreferrer"
          >
            {img}
          </a>
          {id === 'bd' && (
            <>
              <BaseField label="Product kind">
                <Radio.Group
                  options={[
                    { label: 'Full', value: 'full' },
                    { label: 'Full image', value: 'full_img' },
                    { label: 'Summary', value: 'summary' },
                  ]}
                  onChange={(e) =>
                    update({ ...content, product_kind: e.target.value })
                  }
                  value={content.product_kind}
                  optionType="button"
                />
              </BaseField>
              <BaseField label="Product info side">
                <Radio.Group
                  options={[
                    { label: 'Left', value: 'left' },
                    { label: 'Right', value: 'right' },
                  ]}
                  onChange={(e) =>
                    update({ ...content, product_side: e.target.value })
                  }
                  value={content.product_side}
                  optionType="button"
                />
              </BaseField>
            </>
          )}
        </div>
      )
    }
    // Old banner (Can be removed in the future)
    case 'b':
      return (
        <div>This banner is deprecated, please replace it to a new banner</div>
      )
    case 'tri_up':
    case 'tri_down':
    case 'btn':
    case 'btn_arrow':
    case 'btn_primary':
    case 'btn_primary_arrow':
      return (
        <div>
          <BaseField label="Button type">
            <Radio.Group
              value={content.id_tipo}
              onChange={(e) => update({ ...content, id_tipo: e.target.value })}
            >
              {btnTypesArray.map((type) => (
                <Radio.Button key={type} value={type}>
                  {type}
                </Radio.Button>
              ))}
            </Radio.Group>
          </BaseField>
          <Field
            label="Link"
            value={content.path?.[lang]}
            onChange={updatePath}
          />
          {id.startsWith('tri') && (
            <Field
              label="Text"
              value={content.text[lang]}
              onChange={(v) =>
                update({
                  ...content,
                  text: { ...content.text, [lang]: v },
                })
              }
            />
          )}
          <Field
            label="Button Text"
            value={content.btn_text[lang]}
            onChange={(v) =>
              update({
                ...content,
                btn_text: { ...content.btn_text, [lang]: v },
              })
            }
          />
        </div>
      )
    default: {
      console.log('Mising newsletter section', content.id_tipo)
      return null
    }
  }
}

let timeout

export function Content() {
  const [lang, setLang] = useState('es')
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [clipboard, setClipboard] = useClipboard('copy-nl')
  const Ctx = useTraversal()
  const firstRender = useRef(true)
  const initialState = useRef({
    ...Ctx.state.context,
    initial_banner: transformOldInitialBannersToNew(
      Ctx.state.context.initial_banner
    ),
    contenido: Ctx.state.context.contenido.map((s) => ({
      ...s,
      id: s.id || uuid(),
    })),
  })
  const [ctx, setCtx] = useState(initialState.current)
  const [lastSave, setLastSave] = useState(ctx.modification_date)
  const updateContent = update('contenido')
  const lastContentLength = useRef()
  const disabled = disabledStatuses.has(ctx.status)
  const [loading, setLoading] = useState(false)

  // Scroll to bottom after add a new section
  useEffect(() => {
    if (lastContentLength.current < ctx.contenido.length) {
      window.scroll({
        top: document.body.scrollHeight,
        behavior: 'smooth',
      })
    }
    lastContentLength.current = ctx.contenido.length
  }, [ctx.contenido.length])

  function update(field, lang) {
    return (e) =>
      setCtx((s) => {
        const value = e.target?.value !== undefined ? e.target.value : e
        const v = lang ? { ...s[field], [lang]: value } : value
        return { ...s, [field]: v }
      })
  }

  function copy(el) {
    sessionStorage.setItem('copy-nl', JSON.stringify(el))
    setClipboard(el)
    message.success('Copied!')
  }

  function paste() {
    const newSection = { ...clipboard, id: uuid() }
    updateContent([...ctx.contenido, newSection])
    sessionStorage.removeItem('copy-nl')
    setClipboard(null)
  }

  async function verify() {
    const verify = ctx.status !== 'verified'
    const err = () => {
      message.error('There is an error updating the status of the newsletter')
      setVerifying(false)
    }

    setVerifying(true)
    if (dirty) await saveDraft()
    const res = await Ctx.client.verifyNLToggle(ctx.id, verify).catch(err)

    if (res.status >= 300) return err()

    setVerifying(false)
    message.success(
      'Content verified successfully. This newsletter is ready for send.'
    )
    Ctx.refresh()
  }

  async function saveDraft() {
    clearTimeout(timeout)
    setSaving(true)

    const { status } = await Ctx.client.patch(Ctx.path, {
      ...ctx,
      status: undefined,
    })
    if (status === 204) {
      setLastSave(new Date().toISOString())
      message.success('Newsletter saved successfully')
    } else {
      const err = document.querySelector('.error-message')
      const errSuffix = err ? ` ${err.textContent}` : ''
      message.error('Error saving the newsletter.' + errSuffix)
    }
    setSaving(false)
    setDirty(false)
  }

  // Save changes automatically
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }

    if (!Ctx.hasPerm('guillotina.ModifyContent') || disabled) {
      return
    }

    setDirty(true)
    // 1 minute like Wordpress autosave
    timeout = setTimeout(saveDraft, 60 * 1000)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx])

  function updateDiscount(shop) {
    return (e) =>
      setCtx((s) => {
        const value = e.target.value
        if (s.active_shipping_discount === null) {
          s.active_shipping_discount = {}
        }
        s.active_shipping_discount[shop] = [value]
        return { ...s }
      })
  }
  function updateInitialBanner(shop) {
    return (e) =>
      setCtx((s) => {
        const value = e.target.value
        const checked = e.target.checked

        if (!s.initial_banner) s.initial_banner = {}

        if (!Array.isArray(s.initial_banner[shop])) {
          s.initial_banner[shop] = []
        }

        if (checked) {
          s.initial_banner[shop].push(value)
        } else {
          s.initial_banner[shop] = s.initial_banner[shop].filter((v) => {
            return v !== value
          })
        }
        return { ...s }
      })
  }

  async function translateTexts() {
    setLoading(true)
    const data = await newsletterTranslator(Ctx, ctx)
    console.log(data)
    setCtx(data)
    await saveDraft()
    setLoading(false)
    message.success('Translations updated')
  }

  return (
    <div className="newsletter-view">
      <Field
        disabled={disabled}
        label="Campaign"
        value={ctx.title}
        onChange={(title) => setCtx((c) => ({ ...c, title }))}
      />
      <Affix offsetTop={10}>
        <div className="last-edition">
          {saving ? (
            <SyncOutlined spin />
          ) : (
            <CheckCircleTwoTone twoToneColor="#52c41a" />
          )}{' '}
          Last saved: {dayjs(lastSave).format('DD/MM/YYYY HH:mm:ss')}
        </div>
        <div className="newsletter-controls">
          <I18NField
            defaultLang={lang}
            onChangeLang={setLang}
            Widget={() => null}
          />
          <div className="buttons">
            <Button
              disabled={disabled}
              onClick={() => setCtx(initialState.current)}
              className="is-outlined"
            >
              <span className="icon is-small">
                <i className="fas fa-undo"></i>
              </span>
              <span>Reset</span>
            </Button>
            <Button
              onClick={paste}
              className="is-outlined is-primary"
              disabled={!clipboard || disabled}
            >
              <Icon icon="fas fa-paste" />
            </Button>
            <Dropdown
              disabled={disabled}
              overlay={
                <Menu
                  onClick={({ key }) => {
                    updateContent([
                      ...ctx.contenido,
                      {
                        ...setLangs(''),
                        alt: setLangs(''),
                        btn_text: setLangs(''),
                        content: [],
                        id: uuid(),
                        id_tipo: key,
                        image_src: setLangs(''),
                        link: setLangs(''),
                        path: '',
                        product_kind: 'full',
                        product_side: 'right',
                        referencia: '',
                        template: setLangs(''),
                        text: setLangs(''),
                      },
                    ])
                  }}
                >
                  {Object.entries(contentNames).map(([key, name]) => (
                    <Menu.Item key={key}>Section "{name}"</Menu.Item>
                  ))}
                </Menu>
              }
            >
              <Button className="is-primary is-outlined" disabled={disabled}>
                <span className="icon is-small">
                  <i className="fas fa-plus"></i>
                </span>
                <span>Add Section</span>
              </Button>
            </Dropdown>
            <NLPreview id={ctx.id} />
            <Button
              disabled={ctx.status === 'scheduled' || verifying}
              className={`is-normal ${
                ctx.status === 'draft' ? 'is-success' : 'is-dark'
              }`}
              onClick={verify}
            >
              {ctx.status === 'draft' ? (
                <>
                  <span>Verify content</span>
                  <span className="icon is-small">
                    <i className="fas fa-check"></i>
                  </span>
                </>
              ) : (
                <>
                  <span>Back to draft</span>
                  <span className="icon is-small">
                    <i className="fas fa-pen"></i>
                  </span>
                </>
              )}
            </Button>
            <Button
              title={dirty ? 'Save changes' : 'No changes to save'}
              onClick={saveDraft}
              className={`is-normal ${dirty ? 'is-success' : 'is-dark'}`}
              disabled={disabled || saving || !dirty}
            >
              {saving ? <SyncOutlined spin /> : <SaveOutlined />}
              <span style={{ marginLeft: 5 }}>Save</span>
            </Button>
          </div>
        </div>
      </Affix>
      <Field
        onChange={update('subject', lang)}
        label="Subject"
        disabled={disabled}
        value={ctx.subject?.[lang]}
        className="subject"
      />
      {lang === 'es' ? (
        <div className="newsletter-controls">
          <Button onClick={translateTexts} loading={loading}>
            TRADUCIR
          </Button>
        </div>
      ) : null}
      <hr />
      <Space size="middle">
        <BaseField label="Transport">
          {SHOPS.map((shop) => (
            <tr>
              <td style={{ padding: 10 }}>{shop}</td>
              <td>
                <Radio.Group
                  name={shop}
                  disabled={disabled}
                  defaultValue={
                    ctx.active_shipping_discount?.[shop]?.[0] || 'sd'
                  }
                  buttonStyle="solid"
                  onChange={updateDiscount(shop)}
                >
                  <Radio.Button value="sd">No Discount</Radio.Button>
                  {/* <Radio.Button value="tr100">TR100</Radio.Button> */}
                  <Radio.Button value="dtp">DTP</Radio.Button>
                  {/* <Radio.Button value="prod_disc">
                  Discounted Products
                </Radio.Button> */}
                </Radio.Group>
              </td>
            </tr>
          ))}
        </BaseField>
        <BaseField label="Initial banner">
          {SHOPS.map((shop) => (
            <tr>
              <td style={{ padding: 10 }}>{shop}</td>
              <td>
                <Checkbox
                  value="dtp"
                  onChange={updateInitialBanner(shop)}
                  disabled={disabled}
                  defaultChecked={ctx.initial_banner?.[shop]?.includes('dtp')}
                >
                  DTP
                </Checkbox>
                <Checkbox
                  value="prod_disc"
                  onChange={updateInitialBanner(shop)}
                  disabled={disabled}
                  defaultChecked={ctx.initial_banner?.[shop]?.includes(
                    'prod_disc'
                  )}
                >
                  Discounted Products
                </Checkbox>
              </td>
            </tr>
          ))}
        </BaseField>
      </Space>
      <hr />
      {ctx.contenido.length === 0 && <Empty />}
      <DnD
        list={ctx.contenido}
        id="newsletter-sections-list"
        className={`newsletter-sections-list ${disabled ? 'disabled' : ''}`}
        events={allowToMoveOnlyInAccordionHeader}
        onChange={updateContent}
        getChild={(content, index) => {
          const id = content.id_tipo
          const key = `${id}-${index}`

          let label = contentNames[id]
            ? `${contentNames[id] || ''} (${id})`
            : id

          if (buttonTypes.has(id)) label = `Button (${id})`

          return (
            <AccordionCard
              key={key}
              defaultCollapsed={false}
              title={
                <div className="header-title">
                  <div>
                    <b>{label}: </b>
                    {getContentBrief(content, { lang })}
                  </div>
                  <div>
                    <span onClick={() => copy(content)} title="Copy">
                      <Icon icon="fas fa-copy" />
                    </span>
                    <span
                      onClick={() =>
                        updateContent(
                          ctx.contenido.filter((c, i) => i !== index)
                        )
                      }
                      title="Delete"
                    >
                      <Icon icon="fas fa-trash" />
                    </span>
                  </div>
                </div>
              }
            >
              <NewsletterSection
                content={content}
                id={id}
                lang={lang}
                sectionKey={key}
                update={(section) =>
                  updateContent(
                    ctx.contenido.map((c, i) => (i === index ? section : c))
                  )
                }
              />
            </AccordionCard>
          )
        }}
      />
    </div>
  )
}

// Transform deprecated initial_banner to new one
// Probably this can be removed in the future
function transformOldInitialBannersToNew(initialBanner) {
  if (!initialBanner) return
  return Object.keys(initialBanner).reduce((acc, key) => {
    acc[key] = Array.isArray(initialBanner[key])
      ? initialBanner[key]
      : [initialBanner[key]]
    return acc
  }, {})
}
